import React from 'react';

export function FacetPopup({ open = false, title = '', items = [], selected = [], onToggle, onRefine, onCancel, facetKey = '', conjunction = null, conjunctionValue = null, onConjunctionChange }) {
    const visibleClass = open ? '' : 'hidden';
    const list = Array.isArray(items) ? items : [];
    const sel = Array.isArray(selected) ? selected : [];

    // Flatten grouped facet items if shape is [{ name, result:[{title,count}...] }, ...]
    const flatList = (() => {
        try {
            // New structure: if items is an object with 'result' array
            if (items && typeof items === 'object' && !Array.isArray(items) && Array.isArray(items.result)) {
                return items.result.map(r => ({
                    title: r?.name ?? '',
                    id: r?.id ?? r?.name ?? '',
                    label: r?.name ?? '',
                    count: r?.count ?? 0,
                }));
            }

            if (list.length && list[0] && typeof list[0] === 'object' && 'name' in list[0] && Array.isArray(list[0].result)) {
                return list.flatMap(group => {
                    const gname = group?.name ?? '';
                    const arrRes = Array.isArray(group?.result) ? group.result : [];
                    return arrRes.map(r => ({
                        title: r?.title ?? r?.name ?? '',
                        id: r?.id ?? r?.title ?? r?.name ?? '',
                        label: [gname, r?.title ?? r?.name].filter(Boolean).join(' '),
                        count: r?.count ?? 0,
                    }));
                });
            }
        } catch (e) { }
        return list.map(it => ({
            ...it,
            title: it?.title ?? it?.name ?? '',
            id: it?.id ?? it?.title ?? it?.name ?? '',
        }));
    })();

    // hide items without a title
    const filteredList = flatList.filter(it => String(it?.title ?? '').trim() !== '');
    // search query state and filtered visible list by label/title
    const [q, setQ] = React.useState('');

    // Hangul initial consonant (Choseong) helper
    const toChoseong = React.useCallback((str) => {
        if (!str) return '';
        const CHO_LIST = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
        let out = '';
        for (let i = 0; i < str.length; i++) {
            const ch = str[i];
            const code = ch.charCodeAt(0);
            // Hangul syllables: U+AC00 (44032) .. U+D7A3 (55203)
            if (code >= 0xAC00 && code <= 0xD7A3) {
                const syllableIndex = code - 0xAC00; // 588 * choseong + 28 * jung + jong
                const choIndex = Math.floor(syllableIndex / 588);
                out += CHO_LIST[choIndex] || ch;
            } else {
                // For non-Hangul, keep as-is to allow mixed queries
                out += ch;
            }
        }
        return out;
    }, []);

    const visibleList = React.useMemo(() => {
        const kwRaw = String(q || '').trim();
        const kw = kwRaw.toLowerCase();
        if (!kw) return filteredList;
        const kwCho = toChoseong(kwRaw);
        return filteredList.filter(it => {
            const labelRaw = String(it?.label ?? it?.title ?? '');
            const label = labelRaw.toLowerCase();
            if (label.includes(kw)) return true;
            // Also match Hangul initial-consonant sequence
            const labelCho = toChoseong(labelRaw);
            return labelCho.includes(kwCho);
        });
    }, [filteredList, q, toChoseong]);

    const handleToggle = (val) => {
        if (typeof onToggle === 'function') onToggle(val);
    };

    // Select All helpers (use ids as values)
    // Select All operates on visibleList
    const allValues = visibleList.map(it => it?.id ?? it?.title ?? '').filter(v => v !== '');
    const allSelected = allValues.length > 0 && allValues.every(v => sel.includes(v));
    const handleToggleAll = (e) => {
        const checked = e?.target?.checked ?? !allSelected;
        if (checked) {
            // select all visible values
            allValues.forEach(v => { if (!sel.includes(v)) handleToggle(v); });
        } else {
            // deselect all visible values
            allValues.forEach(v => { if (sel.includes(v)) handleToggle(v); });
        }
    };

    if (!open) return null;

    return (
        <>
            {/* 배경 오버레이 - 뒤 영역 클릭 방지 */}
            <div
                className="fixed inset-0 bg-black/50 z-[100]"
                onClick={() => typeof onCancel === 'function' && onCancel()}
            />
            <div
                className={`more-modal-jcr-category fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[800px] w-[calc(100%-80px)] h-[550px] z-[100] bg-white rounded-xl p-6 shadow-[0px_4px_20px_0px_rgba(0,_0,_0,_0.1)] overflow-hidden min-[800px]:h-[757px] min-[800px]:p-10`}
            >
                <div className="more-header flex justify-between">
                    <h4 className="text-[16px] font-bold min-[800px]:text-[18px]">
                        {title || 'Facet'}
                    </h4>
                    <button onClick={() => typeof onCancel === 'function' && onCancel()} className="cursor-pointer">
                        <img
                            src={`${rmVars.themeUrl}images/clear-icon-333.png`}
                            className="w-3"
                            alt="close"
                        />
                    </button>
                </div>

                <div className="more-search mt-5 min-[800px]:mt-6">
                    <div className="search relative w-full">
                        <input
                            type="text"
                            placeholder={`Search for ${title || 'Facet'}`}
                            className="w-full h-[44px] px-4 pr-[40px] border border-[#E4E8F1] outline-none rounded-lg text-[13px] text-[#333] placeholder:text-[#B4BCCC] hover:border-[#8D9AAF] focus:border-[#8D9AAF] disabled:border-[#E4E8F1] disabled:bg-[#F3F5F9] disabled:text-[#B4BCCC] disabled:cursor-not-allowed"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                        <button
                            className="absolute top-1/2 right-[14px] -translate-y-1/2"
                        >
                            <img
                                src={`${rmVars.themeUrl}images/search-ico-8D9AAF.png`}
                                className="w-[16px]"
                                alt="search icon"
                            />
                        </button>
                    </div>
                </div>

                <div className="more-cont-wrap">
                    <label
                        className="checkbox-wrap group items-center cursor-pointer select-none mt-6 hidden min-[800px]:inline-flex"
                    >
                        <input
                            type="checkbox"
                            className="peer hidden"
                            checked={allSelected}
                            onChange={handleToggleAll}
                            disabled={visibleList.length === 0}
                        />
                        <span
                            className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200"
                        >
                        </span>
                        <span className="ml-[10px] text-[14px] text-[#6C7687]">Select All</span>
                    </label>

                    {/* Dynamic facet list */}
                    <ul className="flex flex-col gap-y-[10px] h-[300px] overflow-y-auto -mx-6 px-6 mt-4 pt-4 pb-4 border-t border-t-[#F5F5F5] min-[800px]:mt-2 min-[800px]:flex-row min-[800px]:flex-wrap gap-x-[24px] min-[800px]:mx-0 min-[800px]:px-0 min-[800px]:pr-3 min-[800px]:py-6 min-[800px]:max-h-[470px] min-[800px]:h-auto">
                        {visibleList.map((it, idx) => {
                            const value = it?.id ?? it?.title ?? '';
                            const label = it?.label ?? it?.title ?? value;
                            const count = it?.count ?? 0;
                            const checked = sel.includes(value);
                            return (
                                <li key={`${value || label}-${idx}`} className="flex justify-between min-[800px]:w-[calc(50%-12px)]">
                                    <label className="checkbox-wrap group inline-flex cursor-pointer select-none">
                                        <input type="checkbox" className="peer hidden" checked={checked} onChange={() => handleToggle(value)} />
                                        <span className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]" />
                                        <span className="ml-[10px] text-[14px] text-[#6C7687]">{label}</span>
                                    </label>
                                    <span className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10">{count}</span>
                                </li>
                            );
                        })}
                    </ul>

                    <ul
                        className="hidden"
                    >
                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >EDUCATION & EDUCATIONAL RESEARCH</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >ECONOMICS</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >HISTORY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >MATHEMATICS</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >MATERIALS SCIENCE, MULTIDISCIPLINARY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >LAW</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >PUBLIC, ENVIRONMENTAL & OCCUPATIONAL HEALTH</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >HUMANITIES, MULTIDISCIPLINARY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >MANAGEMENT</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >12</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >ENGINEERING, ELECTRICAL & ELECTRONIC</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >438</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >PHARMACOLOGY & PHARMACY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >420</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >LANGUAGE & LINGUISTICS</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >419
                            </span>
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >ENVIRONMENTAL SCIENCES</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >406</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >MEDICINE, GENERAL & INTERNAL</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >388</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >BIOCHEMISTRY & MOLECULAR BIOLOGY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >374</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >MATHEMATICS, APPLIED</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >373</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >RELIGION</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >368</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >PHILOSOPHY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >367</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >ONCOLOGY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >364</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >SURGERY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >364</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >NEUROSCIENCES</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >358</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >POLITICAL SCIENCE</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >351</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >BUSINESS</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >342</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >PSYCHIATRY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >327</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >LINGUISTICS</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >322</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >CLINICAL NEUROLOGY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >317</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >PLANT SCIENCES</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >300</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >SOCIAL SCIENCES, INTERDISCIPLINARY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >300</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >COMPUTER SCIENCE, INFORMATION SYSTEMS</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >295</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >SOCIAL SCIENCES, INTERDISCIPLINARY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >300</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >GEOSCIENCES, MULTIDISCIPLINARY</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >281</span
                            >
                        </li>

                        <li
                            className="flex justify-between min-[800px]:w-[calc(50%-12px)]"
                        >
                            <label
                                className="checkbox-wrap group inline-flex cursor-pointer select-none"
                            >
                                <input type="checkbox" className="peer hidden" />
                                <span
                                    className="shrink-0 w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px]"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >CARDIAC & CARDIOVASCULAR SYSTEMS</span
                                >
                            </label>
                            <span
                                className="shrink-0 w-[34px] text-right text-[14px] text-[#6C7687] min-[800px]:w-10"
                            >356</span
                            >
                        </li>
                    </ul>

                    <div
                        className={`${visibleList.length === 0 ? '' : 'hidden'} no-cont border-t border-t-[#E4E8F1] mt-4 pt-4 min-[800px]:mt-2`}
                    >
                        <div
                            className="w-full py-[36px] bg-[#F3F5F9] rounded-xl text-[13px] text-center font-semibold"
                        >
                            ‘<span>{q}</span>’ 검색 결과가 없습니다.
                        </div>
                    </div>
                </div>

                <div
                    className="more-bottom absolute left-0 bottom-0 w-full h-[125px] bg-white px-6 pt-5 pb-6 border-t border-t-[#F5F5F5] min-[800px]:h-[96px] min-[800px]:flex items-center justify-end gap-6"
                >
                    <div
                        className="opt-cks flex gap-4 items-center min-[800px]:gap-5"
                    >
                        {conjunction === 'and' && (
                            <label
                                className="checkbox-wrap group inline-flex items-center cursor-pointer select-none"
                            >
                                <input
                                    type="checkbox"
                                    className="peer hidden"
                                    checked={conjunctionValue === 'and'}
                                    onChange={() => {
                                        if (typeof onConjunctionChange === 'function') {
                                            onConjunctionChange(facetKey, conjunctionValue === 'and' ? null : 'and');
                                        }
                                    }}
                                />
                                <span
                                    className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200"
                                >
                                </span>
                                <span className="ml-[10px] text-[14px] text-[#6C7687]"
                                >AND Condition</span
                                >
                            </label>
                        )}
                        {/* Exclude 주석 처리 */}
                        {/* <label
                            className="checkbox-wrap group inline-flex items-center cursor-pointer select-none"
                        >
                            <input type="checkbox" className="peer hidden" />
                            <span
                                className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200"
                            >
                            </span>
                            <span className="ml-[10px] text-[14px] text-[#6C7687]"
                            >Exclude</span
                            >
                        </label> */}
                    </div>

                    <div className="btns mt-4 flex gap-2 min-[800px]:mt-0">
                        <button
                            className="btn-more-cancel w-[calc(50%-4px)] h-12 flex items-center justify-center text-center text-[14px] font-semibold text-[#6C7687] bg-[#F3F5F9] rounded-lg min-[800px]:w-auto min-[800px]:px-6 cursor-pointer"
                            onClick={() => typeof onCancel === 'function' && onCancel()}
                        >
                            Cancel
                        </button>
                        <button
                            className="w-[calc(50%-4px)] h-12 flex items-center justify-center text-center text-[14px] font-semibold text-[#fff] bg-[#4B4DFF] rounded-lg min-[800px]:w-auto min-[800px]:px-6 cursor-pointer"
                            onClick={() => typeof onRefine === 'function' && onRefine()}
                        >
                            Refine
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}